<script setup lang="ts">
/**
 * A pannable, zoomable SVG viewport in world units — a floor plan, a site
 * map, a drawing. The caller draws in its own units (e.g. metres, y pointing
 * down) into the default slot; the canvas puts them on screen.
 *
 * - **Pan:** drag on empty space (`drag-pan`), middle mouse, Space + drag, two
 *   fingers, arrow keys (Shift = bigger steps), or the wheel with `wheel="pan"`.
 * - **Zoom:** wheel or trackpad pinch (towards the cursor), two-finger pinch,
 *   the built-in `NxZoomControl`, `+` / `-`; `0` fits `contentBounds`.
 * - **Grid:** minor and major lines that pick a round spacing for the zoom
 *   level (`planGridSteps`); a scale bar shows a round length.
 * - **Layers:** the default slot is an SVG group in world units, `overlay`
 *   one in screen pixels (handles of constant size), `markers` an HTML layer
 *   over the whole viewport in screen pixels (buttons at world points —
 *   `NxPlanMarker`; the layer lets presses through, its children take them),
 *   `controls` an HTML layer at the top left (a tool bar). Every slot gets
 *   `pixelsPerUnit`, `worldToScreen`, `screenToWorld`, `px(n)` (world length
 *   of n screen pixels, for zoom-independent strokes), `visibleBounds`,
 *   `grid` and `view`.
 * - **Tools:** `pointerdown` / `pointermove` / `pointerup` / `pointercancel`
 *   carry the world point and the modifiers. A `pointerdown` handler that
 *   calls `event.originalEvent.preventDefault()` claims the press for its
 *   tool, so the canvas does not pan (as does turning `drag-pan` off). While a
 *   tool holds the press, moves and the release follow it even outside.
 *
 * `v-model:view` (`{ center, zoom }`) lets the caller persist the view; zoom
 * is a factor on `unitPx` (screen pixels per world unit at 100 %).
 */
import { computed, inject, onMounted, ref, useId, watch } from 'vue';
import { isEditableTarget, useElementSize } from '../Composables';
import { i18nInjectionKey } from '../internal';
import { NxZoomControl } from '../ZoomControl';
import type { NxPlanBounds, NxPlanPoint } from './geometry';
import {
  clampZoom,
  fitView,
  panView,
  planGridSteps,
  planScaleBar,
  planTransform,
  zoomViewAt,
  type NxPlanView,
} from './view';
import type { NxPlanPointerEvent, NxPlanSlotProps } from './types';

const props = withDefaults(
  defineProps<{
    /** Screen pixels per world unit at zoom 1 (100 %). */
    unitPx?: number;
    minZoom?: number;
    maxZoom?: number;
    /** What fitting (`0`, „Einpassen“, `fit()`) shows. */
    contentBounds?: NxPlanBounds | null;
    /** Screen pixels kept free around the content when fitting. */
    fitPadding?: number;
    /** Fit `contentBounds` once the viewport has a size. */
    fitOnMount?: boolean;
    /** Show the grid. */
    grid?: boolean;
    /** Minimum distance of minor grid lines on screen, in px. */
    gridMinPx?: number;
    /** Show the scale bar. */
    scaleBar?: boolean;
    /** Widest the scale bar gets, in px. */
    scaleBarMaxPx?: number;
    /** Unit printed after the scale-bar length. */
    unit?: string;
    /** Formats the scale-bar length (world units) instead of „number unit“. */
    formatLength?: (length: number) => string;
    /** Show the zoom control at the bottom right. */
    zoomControl?: boolean;
    /** Zoom factor of one step (+/-, zoom control). */
    zoomStep?: number;
    /** Plain wheel zooms (mouse) or pans (trackpad); Ctrl/⌘ + wheel always zooms. */
    wheel?: 'zoom' | 'pan';
    /** Dragging empty space with the primary button pans. */
    dragPan?: boolean;
    /** Arrow-key pan step in px (four times with Shift). */
    panStep?: number;
    /** Accessible name of the canvas. */
    label?: string;
    /** Keyboard hint read out with the canvas. */
    hint?: string;
    zoomInLabel?: string;
    zoomOutLabel?: string;
    resetZoomLabel?: string;
    fitLabel?: string;
    disabled?: boolean;
    locale?: string;
  }>(),
  {
    unitPx: 50,
    minZoom: 0.05,
    maxZoom: 20,
    contentBounds: null,
    fitPadding: 32,
    fitOnMount: false,
    grid: true,
    gridMinPx: 12,
    scaleBar: true,
    scaleBarMaxPx: 120,
    unit: 'm',
    formatLength: undefined,
    zoomControl: true,
    zoomStep: 1.25,
    wheel: 'zoom',
    dragPan: true,
    panStep: 48,
    label: 'Plan',
    hint: 'Ziehen oder Pfeiltasten verschieben, Mausrad, Plus und Minus zoomen, 0 passt ein.',
    zoomInLabel: undefined,
    zoomOutLabel: undefined,
    resetZoomLabel: undefined,
    fitLabel: undefined,
    disabled: false,
    locale: undefined,
  }
);

const view = defineModel<NxPlanView>('view', {
  default: () => ({ center: { x: 0, y: 0 }, zoom: 1 }),
});

const emit = defineEmits<{
  pointerdown: [event: NxPlanPointerEvent];
  pointermove: [event: NxPlanPointerEvent];
  pointerup: [event: NxPlanPointerEvent];
  pointercancel: [event: NxPlanPointerEvent];
}>();

defineSlots<{
  default?: (props: NxPlanSlotProps) => unknown;
  overlay?: (props: NxPlanSlotProps) => unknown;
  markers?: (props: NxPlanSlotProps) => unknown;
  controls?: (props: NxPlanSlotProps) => unknown;
}>();

const root = ref<HTMLElement | null>(null);
const svg = ref<SVGSVGElement | null>(null);
const hintId = useId();
const { width, height } = useElementSize(root);
const size = computed(() => ({ width: width.value, height: height.value }));
const limits = computed(() => ({ minZoom: props.minZoom, maxZoom: props.maxZoom }));
const frame = computed(() => ({ ...limits.value, size: size.value, unitPx: props.unitPx }));

// The view the canvas works with: follows the model, and is written first so
// several steps within one event build on each other before the parent echoes.
const current = ref<NxPlanView>(normalize(view.value));
watch(view, (next) => {
  current.value = normalize(next);
});

function normalize(next: NxPlanView): NxPlanView {
  return { center: { ...next.center }, zoom: clampZoom(next.zoom, limits.value) };
}

function setView(next: NxPlanView) {
  if (
    next.zoom === current.value.zoom &&
    next.center.x === current.value.center.x &&
    next.center.y === current.value.center.y
  ) {
    return;
  }
  current.value = next;
  view.value = { center: { ...next.center }, zoom: next.zoom };
}

const transform = computed(() => planTransform(current.value, size.value, props.unitPx));
const gridSteps = computed(() => planGridSteps(transform.value.pixelsPerUnit, props.gridMinPx));

const slotProps = computed<NxPlanSlotProps>(() => ({
  pixelsPerUnit: transform.value.pixelsPerUnit,
  worldToScreen: transform.value.worldToScreen,
  screenToWorld: transform.value.screenToWorld,
  px: transform.value.px,
  visibleBounds: transform.value.visibleBounds,
  grid: gridSteps.value,
  view: current.value,
  size: size.value,
}));

// ── Grid ────────────────────────────────────────────────────────────────────
const MAX_LINES = 400;

const gridPaths = computed(() => {
  if (!props.grid || !width.value || !height.value) return null;
  const { minor, major } = gridSteps.value;
  const ratio = Math.max(1, Math.round(major / minor));
  const { visibleBounds: b, worldToScreen } = transform.value;
  let minorD = '';
  let majorD = '';
  const x0 = Math.ceil(b.minX / minor);
  const x1 = Math.floor(b.maxX / minor);
  const y0 = Math.ceil(b.minY / minor);
  const y1 = Math.floor(b.maxY / minor);
  if (x1 - x0 + (y1 - y0) > MAX_LINES) return null;
  // Lines sit on half pixels so a hairline stays one crisp device pixel.
  const crisp = (v: number) => Math.round(v) + 0.5;
  for (let k = x0; k <= x1; k++) {
    const sx = crisp(worldToScreen({ x: k * minor, y: 0 }).x);
    const line = `M${sx} 0V${height.value}`;
    if (((k % ratio) + ratio) % ratio === 0) majorD += line;
    else minorD += line;
  }
  for (let k = y0; k <= y1; k++) {
    const sy = crisp(worldToScreen({ x: 0, y: k * minor }).y);
    const line = `M0 ${sy}H${width.value}`;
    if (((k % ratio) + ratio) % ratio === 0) majorD += line;
    else minorD += line;
  }
  return { minor: minorD, major: majorD };
});

// ── Scale bar ───────────────────────────────────────────────────────────────
const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const numberFormat = computed(
  () => new Intl.NumberFormat(locale.value, { maximumFractionDigits: 3 })
);
const scale = computed(() => planScaleBar(transform.value.pixelsPerUnit, props.scaleBarMaxPx));
const scaleLabel = computed(() =>
  props.formatLength
    ? props.formatLength(scale.value.length)
    : `${numberFormat.value.format(scale.value.length)} ${props.unit}`.trim()
);

// ── View operations ─────────────────────────────────────────────────────────
function viewportCenter(): NxPlanPoint {
  return { x: width.value / 2, y: height.value / 2 };
}

/** Zooms by `factor` around a screen point (default: the viewport centre). */
function zoomBy(factor: number, anchor: NxPlanPoint = viewportCenter()) {
  setView(zoomViewAt(current.value, factor, anchor, frame.value));
}

/** Sets the zoom factor, keeping the centre. */
function setZoom(zoom: number) {
  setView({ center: { ...current.value.center }, zoom: clampZoom(zoom, limits.value) });
}

/** Moves the content by screen pixels. */
function panBy(dx: number, dy: number) {
  setView(panView(current.value, dx, dy, props.unitPx));
}

/** Puts a world point into the middle of the viewport. */
function centerOn(point: NxPlanPoint) {
  setView({ center: { ...point }, zoom: current.value.zoom });
}

let pendingFit: NxPlanBounds | null | undefined;

/**
 * Shows `bounds` (default `contentBounds`) completely. Without any bounds it
 * goes back to 100 % around the current centre. Before the viewport has a
 * size the fit waits for it.
 */
function fit(bounds?: NxPlanBounds | null) {
  const target = bounds ?? props.contentBounds;
  if (!width.value || !height.value) {
    pendingFit = target;
    return;
  }
  if (!target) {
    setZoom(1);
    return;
  }
  setView(
    fitView(target, frame.value, { padding: props.fitPadding, fallbackZoom: current.value.zoom })
  );
}

watch(size, (next) => {
  if (pendingFit !== undefined && next.width && next.height) {
    const target = pendingFit;
    pendingFit = undefined;
    fit(target);
  }
});

onMounted(() => {
  if (props.fitOnMount) fit();
});

watch(limits, () => {
  const zoom = clampZoom(current.value.zoom, limits.value);
  if (zoom !== current.value.zoom) setZoom(zoom);
});

/** Screen point (relative to the viewport) of a client-coordinate position. */
function clientToScreen(clientX: number, clientY: number): NxPlanPoint {
  const rect = (svg.value ?? root.value)?.getBoundingClientRect();
  return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) };
}

/** World point of a client-coordinate position — e.g. where something was dropped. */
function clientToWorld(clientX: number, clientY: number): NxPlanPoint {
  return transform.value.screenToWorld(clientToScreen(clientX, clientY));
}

defineExpose({ fit, zoomBy, setZoom, panBy, centerOn, clientToWorld, transform });

// ── Pointer ─────────────────────────────────────────────────────────────────
type Gesture = 'tool' | 'pan' | 'pinch' | null;

/** Screen pixels a press may move and still count as a click, not a pan. */
const CLICK_SLOP = 3;

const pointers = new Map<number, NxPlanPoint>();
const gesture = ref<Gesture>(null);
let gesturePointer: number | null = null;
let panStart: NxPlanPoint | null = null;
let panned = false;
/** Whether the tools saw the press of the current gesture (not so for middle-button or Space pans). */
let announced = false;
let pinch: { mid: NxPlanPoint; dist: number } | null = null;
const spaceHeld = ref(false);

function toPlanEvent(event: PointerEvent, screen: NxPlanPoint): NxPlanPointerEvent {
  return {
    world: transform.value.screenToWorld(screen),
    screen,
    pixelsPerUnit: transform.value.pixelsPerUnit,
    pointerId: event.pointerId ?? 1,
    pointerType: event.pointerType || 'mouse',
    button: event.button,
    buttons: event.buttons,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    panned,
    originalEvent: event,
  };
}

function capture(event: PointerEvent) {
  try {
    svg.value?.setPointerCapture?.(event.pointerId);
  } catch {
    // Synthetic or already released pointers cannot be captured.
  }
}

function release(event: PointerEvent) {
  try {
    if (svg.value?.hasPointerCapture?.(event.pointerId)) {
      svg.value.releasePointerCapture(event.pointerId);
    }
  } catch {
    // See capture().
  }
}

function pinchState(): { mid: NxPlanPoint; dist: number } | null {
  const [a, b] = [...pointers.values()];
  if (!a || !b) return null;
  return {
    mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    dist: Math.hypot(b.x - a.x, b.y - a.y),
  };
}

function onPointerDown(event: PointerEvent) {
  if (props.disabled) return;
  const screen = clientToScreen(event.clientX, event.clientY);
  const id = event.pointerId ?? 1;
  pointers.set(id, screen);
  // Take the keyboard on press (the tools may cancel the native focus), but
  // without the keyboard focus ring.
  if (root.value && !root.value.contains(document.activeElement)) {
    root.value.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
  }

  if (pointers.size === 2) {
    // A second finger turns whatever the first one did into a pinch.
    if (announced && gesturePointer !== null) {
      emit('pointercancel', toPlanEvent(event, screen));
      announced = false;
    }
    gesture.value = 'pinch';
    gesturePointer = null;
    pinch = pinchState();
    capture(event);
    event.preventDefault();
    return;
  }
  if (pointers.size > 2 || gesture.value) return;

  panned = false;
  announced = false;
  if (event.button === 1 || (spaceHeld.value && event.button === 0)) {
    event.preventDefault();
    gesture.value = 'pan';
    gesturePointer = id;
    panStart = screen;
    capture(event);
    return;
  }
  emit('pointerdown', toPlanEvent(event, screen));
  announced = true;
  if (event.button !== 0) return;
  gesturePointer = id;
  panStart = screen;
  gesture.value = !event.defaultPrevented && props.dragPan ? 'pan' : 'tool';
  capture(event);
}

function onPointerMove(event: PointerEvent) {
  if (props.disabled) return;
  const id = event.pointerId ?? 1;
  const screen = clientToScreen(event.clientX, event.clientY);
  const previous = pointers.get(id);
  if (previous) pointers.set(id, screen);

  if (gesture.value === 'pinch') {
    const next = pinchState();
    if (!next || !pinch) return;
    let moved = panView(
      current.value,
      next.mid.x - pinch.mid.x,
      next.mid.y - pinch.mid.y,
      props.unitPx
    );
    if (pinch.dist > 0 && next.dist > 0) {
      moved = zoomViewAt(moved, next.dist / pinch.dist, next.mid, frame.value);
    }
    pinch = next;
    setView(moved);
    return;
  }
  if (gesture.value === 'pan' && id === gesturePointer && previous) {
    if (!panned && panStart) {
      if (Math.hypot(screen.x - panStart.x, screen.y - panStart.y) < CLICK_SLOP) return;
      panned = true;
      panBy(screen.x - panStart.x, screen.y - panStart.y);
      return;
    }
    panBy(screen.x - previous.x, screen.y - previous.y);
    return;
  }
  if (gesture.value === 'tool' && id !== gesturePointer) return;
  emit('pointermove', toPlanEvent(event, screen));
}

function endPointer(event: PointerEvent, cancelled: boolean) {
  const id = event.pointerId ?? 1;
  if (!pointers.has(id)) return;
  pointers.delete(id);
  release(event);
  const screen = clientToScreen(event.clientX, event.clientY);

  if (gesture.value === 'pinch') {
    if (pointers.size === 0) {
      gesture.value = null;
      pinch = null;
    } else {
      pinch = pinchState();
    }
    return;
  }
  if (id !== gesturePointer) return;
  gesture.value = null;
  gesturePointer = null;
  panStart = null;
  if (announced) {
    if (cancelled) emit('pointercancel', toPlanEvent(event, screen));
    else emit('pointerup', toPlanEvent(event, screen));
  }
  announced = false;
  panned = false;
}

function onPointerUp(event: PointerEvent) {
  endPointer(event, false);
}

function onPointerCancel(event: PointerEvent) {
  endPointer(event, true);
}

// ── Wheel ───────────────────────────────────────────────────────────────────
function onWheel(event: WheelEvent) {
  if (props.disabled) return;
  event.preventDefault();
  const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? height.value || 400 : 1;
  const anchor = clientToScreen(event.clientX, event.clientY);
  const zoom = event.ctrlKey || event.metaKey || (props.wheel === 'zoom' && !event.shiftKey);
  if (zoom) {
    // Trackpad pinches arrive as Ctrl + small deltas, mouse wheels as big ones.
    const k = event.ctrlKey ? 0.01 : 0.0015;
    const factor = Math.min(2, Math.max(0.5, Math.exp(-event.deltaY * unit * k)));
    zoomBy(factor, anchor);
    return;
  }
  const horizontal = event.shiftKey && !event.deltaX;
  panBy(
    -(horizontal ? event.deltaY : event.deltaX) * unit,
    -(horizontal ? 0 : event.deltaY) * unit
  );
}

// ── Keyboard ────────────────────────────────────────────────────────────────
function ownsKey(event: KeyboardEvent): boolean {
  const target = event.target as Node | null;
  return target === root.value || !!(target && svg.value?.contains(target));
}

function onKeydown(event: KeyboardEvent) {
  if (props.disabled || event.defaultPrevented || !ownsKey(event) || isEditableTarget(event)) {
    return;
  }
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  const step = event.shiftKey ? props.panStep * 4 : props.panStep;
  switch (event.key) {
    case '+':
    case '=':
      zoomBy(props.zoomStep);
      break;
    case '-':
    case '_':
      zoomBy(1 / props.zoomStep);
      break;
    case '0':
      fit();
      break;
    case 'ArrowLeft':
      panBy(step, 0);
      break;
    case 'ArrowRight':
      panBy(-step, 0);
      break;
    case 'ArrowUp':
      panBy(0, step);
      break;
    case 'ArrowDown':
      panBy(0, -step);
      break;
    case ' ':
      spaceHeld.value = true;
      break;
    default:
      return;
  }
  event.preventDefault();
}

function onKeyup(event: KeyboardEvent) {
  if (event.key === ' ') spaceHeld.value = false;
}

function onBlur() {
  spaceHeld.value = false;
}

const cursorClass = computed(() => {
  if (gesture.value === 'pan' || gesture.value === 'pinch') return 'is-panning';
  if (spaceHeld.value || (props.dragPan && !props.disabled)) return 'can-pan';
  return null;
});
</script>

<template>
  <div
    ref="root"
    class="nx-plan-canvas"
    :class="[cursorClass, { 'is-disabled': props.disabled }]"
    role="application"
    :aria-label="props.label"
    :aria-describedby="hintId"
    :aria-disabled="props.disabled || undefined"
    :tabindex="props.disabled ? -1 : 0"
    data-testid="nx-plan-canvas"
    @keydown="onKeydown"
    @keyup="onKeyup"
    @blur="onBlur"
  >
    <svg
      ref="svg"
      class="nx-plan-canvas__svg"
      :width="width || undefined"
      :height="height || undefined"
      :viewBox="width && height ? `0 0 ${width} ${height}` : undefined"
      data-testid="nx-plan-canvas-svg"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
      @wheel="onWheel"
      @contextmenu="gesture === 'pan' && $event.preventDefault()"
    >
      <g v-if="gridPaths" class="nx-plan-canvas__grid" aria-hidden="true">
        <path class="nx-plan-canvas__grid-minor" :d="gridPaths.minor" />
        <path class="nx-plan-canvas__grid-major" :d="gridPaths.major" />
      </g>
      <g class="nx-plan-canvas__world" :transform="transform.svgTransform">
        <slot v-bind="slotProps" />
      </g>
      <g class="nx-plan-canvas__overlay">
        <slot name="overlay" v-bind="slotProps" />
      </g>
    </svg>
    <!-- The wheel over a marker still zooms the plan. -->
    <div
      v-if="$slots.markers"
      class="nx-plan-canvas__markers"
      data-testid="nx-plan-canvas-markers"
      @wheel="onWheel"
    >
      <slot name="markers" v-bind="slotProps" />
    </div>
    <div v-if="$slots.controls" class="nx-plan-canvas__controls">
      <slot name="controls" v-bind="slotProps" />
    </div>
    <div
      v-if="props.scaleBar && scale.length > 0"
      class="nx-plan-canvas__scale"
      data-testid="nx-plan-canvas-scale"
    >
      <span class="nx-plan-canvas__scale-bar" :style="{ width: `${scale.px}px` }" />
      <span class="nx-plan-canvas__scale-label">{{ scaleLabel }}</span>
    </div>
    <NxZoomControl
      v-if="props.zoomControl"
      class="nx-plan-canvas__zoom"
      :model-value="current.zoom"
      :min="props.minZoom"
      :max="props.maxZoom"
      :factor="props.zoomStep"
      fittable
      :disabled="props.disabled"
      :locale="locale"
      :zoom-in-label="props.zoomInLabel"
      :zoom-out-label="props.zoomOutLabel"
      :reset-label="props.resetZoomLabel"
      :fit-label="props.fitLabel"
      @update:model-value="setZoom"
      @fit="fit()"
    />
    <span :id="hintId" class="nx-plan-canvas__hint">{{ props.hint }}</span>
  </div>
</template>

<style scoped>
.nx-plan-canvas {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: var(--nxt-area-size-lg);
  overflow: hidden;
  container-type: inline-size;
  border-radius: inherit;
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  outline: none;
  user-select: none;
}
.nx-plan-canvas:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-plan-canvas__svg {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
}
.nx-plan-canvas.can-pan .nx-plan-canvas__svg {
  cursor: grab;
}
.nx-plan-canvas.is-panning .nx-plan-canvas__svg {
  cursor: grabbing;
}
.nx-plan-canvas.is-disabled {
  opacity: 0.7;
}
.nx-plan-canvas__grid path {
  fill: none;
  stroke-width: var(--nxt-hairline);
  shape-rendering: crispEdges;
}
.nx-plan-canvas__grid-minor {
  stroke: var(--nxt-border-subtle);
}
.nx-plan-canvas__grid-major {
  stroke: var(--nxt-border);
}
/* Presses on empty space fall through to the plan; markers take their own. */
.nx-plan-canvas__markers {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}
.nx-plan-canvas__controls {
  position: absolute;
  top: var(--nxt-space-sm);
  left: var(--nxt-space-sm);
  display: flex;
  gap: var(--nxt-space-xs);
  align-items: center;
}
.nx-plan-canvas__scale {
  position: absolute;
  bottom: var(--nxt-space-sm);
  left: var(--nxt-space-sm);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  align-items: flex-start;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  background: color-mix(in srgb, var(--nxt-surface-raised-bg) 85%, transparent);
  pointer-events: none;
}
.nx-plan-canvas__scale-bar {
  height: calc(var(--nxt-hairline) * 5);
  border: calc(var(--nxt-hairline) * 1.5) solid var(--nxt-fg);
  border-top: none;
}
.nx-plan-canvas__scale-label {
  color: var(--nxt-text-muted-fg);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  line-height: var(--nxt-line-height-tight);
}
.nx-plan-canvas__zoom {
  position: absolute;
  right: var(--nxt-space-sm);
  bottom: var(--nxt-space-sm);
}
/* A narrow canvas (a phone) has no room for both in one row: the scale bar
   goes above the zoom control. */
@container (max-width: 30rem) {
  .nx-plan-canvas__scale {
    bottom: calc(var(--nxt-space-sm) * 2 + var(--nxt-control-size-md));
  }
}
.nx-plan-canvas__hint {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
@media (forced-colors: active) {
  .nx-plan-canvas__grid-minor,
  .nx-plan-canvas__grid-major {
    stroke: GrayText;
  }
  .nx-plan-canvas__scale-bar {
    border-color: CanvasText;
  }
}
</style>
